/**
 * watchHistoryEnrichment — pure transforms that turn `UserMediaPosition`-like
 * entries (from either `userMediaPositionList` or
 * `UserProfile.watchHistory`) into `EnrichedPlaylistItem[]` for rendering
 * inside `SystemPlaylistRow` / `ResponsivePlaylistDisplay`.
 *
 * Watch History renders FLAT — one tile per watched video — to match how
 * custom playlists render in the same surface. Episodes-of-series are NOT
 * pivoted to series-shaped tiles, and duplicate-by-series collapse is NOT
 * applied. The series title/path/id are still surfaced on the video tile so
 * hover metadata can show the parent series for context.
 *
 * Extracted from `useWatchHistoryData` so the profile-page activity-history
 * section can render the same item shape without duplicating the logic.
 */

import _get from 'lodash/get'
import {
  buildTileData,
  toImmutableTile,
  flattenReferences,
} from './content-utils'
import type {
  UserHistoryEntry,
  TileDataShape,
  PlaylistContentItem,
  EnrichedPlaylistItem,
} from '../../types'

// Re-export `flattenReferences` so existing imports of
// `services/community/identity/library/watchHistoryEnrichment` keep
// resolving. The implementation lives in `content-utils.ts` (used by
// `buildTileData` there); this module used to duplicate it byte-for-byte.
export { flattenReferences }

export function buildTileFromEntry (
  entry: UserHistoryEntry,
  contentMap?: ReadonlyMap<string, PlaylistContentItem>,
): TileDataShape | null {
  const featured = _get(entry, 'media.contentFeatured[0]', null)
  if (!featured) return null

  // When the hydrated full Content is available, use it as the source for
  // tile construction so `buildTileData` sees `featureMedia.mediaAssets.
  // aiMetadata.learnerLevel` — PR 2 removed that selection from
  // GET_USER_HISTORY / GET_PROFILE_WATCH_HISTORY_CAROUSEL to keep the
  // ai-service tail off the critical path; useContentHydration restores
  // it via cache-and-network GET_CONTENT.  Pre-hydration renders fall
  // back to the embedded `featured` (current behaviour, no pill); post-
  // hydration renders pick up the pill in-place without layout shift.
  const cid = featured?.contentId != null ? String(featured.contentId) : ''
  const ctype = featured?.contentType ? String(featured.contentType) : ''
  const hydrationKey = cid && ctype ? `${cid}-${ctype}` : ''
  const hydrated = hydrationKey && contentMap ? contentMap.get(hydrationKey) : undefined
  const tileSource = hydrated ?? (featured as unknown as PlaylistContentItem)

  // Single source of truth — `buildTileData` produces the same `tileData`
  // shape custom-playlist tiles consume (learnerLevelLabel, vote,
  // commentsCount, featureMedia.contentPolicy.accessCheck.allowed,
  // featureMedia.mediaAssets.aiMetadata.learnerLevel.level, all the alias
  // fields the tile component reads).  Watch History renders flat —
  // episode-shaped tiles, no series pivot — so we hand the EPISODE
  // (`tileSource`) straight in.  The cast bridges the GraphQL `Content`
  // shape to the `PlaylistContentItem` parameter type; both are
  // structurally compatible for the fields `buildTileData` reads.
  const baseTile = buildTileData(tileSource)
  if (!baseTile) return null

  const mediaDuration = _get(entry, 'media.duration', 0)
  const userPosition = entry.position ?? 0
  const mediaId = _get(entry, 'media.mediaId', entry.mediaId)

  // Fallback id when `content.contentId` is missing — preserves the legacy
  // behavior where `buildTileFromEntry` derived a tile identity from the
  // userMediaPosition row's mediaId so each tile is still uniquely
  // identifiable in the flat list.
  const fallbackId = baseTile.contentId ?? baseTile.id ?? mediaId

  // Overlay the userMediaPosition row's media identity + the user's
  // playback position onto `featureMedia` so the in-progress indicator and
  // resume pointer behave correctly. The rest of `featureMedia`
  // (contentPolicy.accessCheck.allowed, mediaAssets.aiMetadata) flows
  // through from the GraphQL response unchanged so the play-button overlay
  // and skill-level pill render.
  const baseFeatureMedia = baseTile.featureMedia ?? null
  return {
    ...baseTile,
    id: fallbackId,
    contentId: fallbackId,
    nid: fallbackId,
    duration: mediaDuration || baseTile.duration,
    featureMedia: {
      ...(baseFeatureMedia ?? {}),
      mediaId: mediaId ?? baseFeatureMedia?.mediaId ?? null,
      duration: mediaDuration || baseFeatureMedia?.duration || 0,
      userMediaPosition: { position: userPosition },
    },
  }
}

/**
 * Coerce a tile's contentId to a finite number (or null). Watch History tiles
 * are video-shaped (no series pivot), so the episode contentId lives at
 * `tileData.contentId` directly. The lookup still falls back to
 * `featuredContent.contentId` defensively in case a producer ever supplies
 * a pivoted tile shape.
 */
function extractEpisodeContentId (tileData: TileDataShape): number | null {
  const raw = tileData.contentId ?? tileData.featuredContent?.contentId
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Map raw `UserMediaPosition`-like entries into rendered playlist items —
 * one tile per entry. Skips entries without a `media.contentFeatured`
 * content reference.
 *
 * No series pivot. Dedupe-by-contentId IS applied: a single Brooklyn video
 * can have multiple `UserMediaPosition` rows (one per media asset — audio,
 * video, transcript) and all of them share the same
 * `media.contentFeatured[0].contentId`. Without dedupe the rendered grid
 * keys would collide (React warns, downstream `tileDataMap.get(key)`
 * picks one arbitrarily) and the same tile would appear several times to
 * the user. The query orders by `lastPlayed DESC`, so first-write-wins
 * preserves the most recent play of any given video.
 *
 * Removing a deduped tile still hides EVERY underlying position row for
 * that video — the `useWatchHistoryData` remove handler fires
 * `flagWatchedVideo(videoId, hiddenByUser:true)` and the Brooklyn
 * resolver flips every row whose contentId matches, then the cache.modify
 * filter walks every cached entry sharing that contentId. So one Remove
 * click is sufficient even though only one of the N rows was visible.
 *
 * `bundledEpisodeContentIds` is retained as an always-single-element array
 * (`[contentId]`) for back-compat with consumers; future cleanup can drop
 * the field once all callsites stop reading it.
 */
export function enrichWatchHistoryItems (
  entries: UserHistoryEntry[] | null | undefined,
  contentMap?: ReadonlyMap<string, PlaylistContentItem>,
): EnrichedPlaylistItem[] {
  const built: EnrichedPlaylistItem[] = []
  const seenContentIds = new Set<string>()
  const list = entries || []
  list.forEach((entry) => {
    const tileData = buildTileFromEntry(entry, contentMap)
    if (!tileData) return
    const contentId = String(tileData.contentId || tileData.id || '')
    // Skip duplicates of the same video.  contentId is the React key
    // downstream (`PlaylistTileGrid` uses `item.contentId || item.id`),
    // so duplicates would warn and trigger the unsupported-key-collision
    // codepath.  Empty contentId (defensive — `buildTileFromEntry` should
    // have skipped already) also bails so we never insert `id: ''`.
    if (!contentId || seenContentIds.has(contentId)) return
    seenContentIds.add(contentId)
    const episodeContentId = extractEpisodeContentId(tileData)

    built.push({
      id: contentId,
      contentId,
      // Watch-history entries aren't playlist-item rows — they're removed
      // via `flagWatchedVideo`, not `playlistDeletePlaylistItem` — so the
      // row identifiers are intentionally null.
      playlistItemContentId: null,
      playlistItemContentType: null,
      // Watch-history entries are not user-orderable — playlistPosition
      // does not apply.  Field is set to satisfy the interface.
      playlistPosition: null,
      bundledEpisodeContentIds: episodeContentId != null ? [episodeContentId] : [],
      title: tileData.title || '',
      path: tileData.path || '',
      imageUrl: tileData.hero || tileData.poster || '',
      isAssetUrl: Boolean(tileData.hero),
      duration: tileData.duration || 0,
      tileData,
      tileDataImmutable: toImmutableTile(tileData),
    })
  })
  return built
}
